import type { Features } from "@/libs/feature-toggle"
import { checkDesktop } from "@/libs/hardware/desktop"
import { imageBitmapToPixels } from "@/libs/image"
import { defaultIllumination, type Preset } from "./face/preset"
import { PROBLEM } from "./face/stages"
import { createImageValidator } from "./face/validation-image"

export interface ImageQualityOptions {
  isUpload: boolean
  isFace180: boolean
  features?: Features
}

let qualityCheckPreset: Preset | null = null

export interface ProcessedQualityCheck {
  stageTitle: string
  stageDescription: string[]
  hasProblem: boolean
  allowToContinue: boolean
  result: Set<PROBLEM>
}

export interface ImageQualityTranslations {
  [key: string]: { title: string; description: string } | undefined
}

const QUALITY_PROBLEM_TO_EVENT_CONTENT: Record<
  PROBLEM,
  | "no_face_detected"
  | "noise"
  | "blur"
  | "incorrect_face_position"
  | "poor_lighting"
  | "low_resolution"
  | null
> = {
  [PROBLEM.FACE_DISPLACED]: "no_face_detected",
  [PROBLEM.QUALITY_NOISE]: "noise",
  [PROBLEM.QUALITY_BLUR]: "blur",
  [PROBLEM.FACE_PITCH_ANGLE_DEVIATION]: "incorrect_face_position",
  [PROBLEM.FACE_YAW_ANGLE_DEVIATION]: "incorrect_face_position",
  [PROBLEM.FACE_ROLL_ANGLE_DEVIATION]: "incorrect_face_position",
  [PROBLEM.LIGHTING_INSUFFICIENT]: "poor_lighting",
  [PROBLEM.LIGHTING_EXCESSIVE]: "poor_lighting",
  [PROBLEM.LIGHTING_ANGLE_DEVIATION]: "poor_lighting",
  [PROBLEM.QUALITY_RESOLUTION]: "low_resolution",
  [PROBLEM.QUALITY_FULL_FACE]: null,
  [PROBLEM.LOW_IMAGE_QUALITY]: null,
}

export const getQualityCheckAnalyticsEvents = (problems: Set<PROBLEM>) => {
  const eventContents = new Set<
    | "no_face_detected"
    | "noise"
    | "blur"
    | "incorrect_face_position"
    | "poor_lighting"
    | "low_resolution"
  >()

  for (const problem of problems) {
    const eventContent = QUALITY_PROBLEM_TO_EVENT_CONTENT[problem]
    if (eventContent) {
      eventContents.add(eventContent)
    }
  }

  return eventContents.size > 0 ? { event_content: Array.from(eventContents) } : null
}

export const getQualityProblemEventContent = (problem: PROBLEM) => {
  return QUALITY_PROBLEM_TO_EVENT_CONTENT[problem]
}

export const checkFacePresenceOnly = async (
  image: File | ImageBitmap,
  features?: Features,
): Promise<Set<PROBLEM>> => {
  if (!qualityCheckPreset)
    qualityCheckPreset = (await import("@/features/presets/preset").then((preset) =>
      preset.create({ staticImage: true }),
    )) as Preset

  const blankImage = new ImageData(1, 1)
  const blankImageSource = await createImageBitmap(blankImage)
  await qualityCheckPreset.getDetections(blankImageSource)
  blankImageSource.close?.()

  const imageSource = await createImageBitmap(image)
  const width = imageSource.width
  const height = imageSource.height
  const face = await qualityCheckPreset.getDetections(imageSource)
  imageSource.close?.()

  const faceCheckEnabled = features?.isOn("face-check") ?? false
  
  if (faceCheckEnabled && !face) {
    return new Set([PROBLEM.FACE_DISPLACED])
  }
  
  return new Set()
}

export const checkImageQuality = async (
  image: File | ImageBitmap,
  options: ImageQualityOptions,
): Promise<Set<PROBLEM>> => {
  const { isFace180, isUpload, features } = options
  if (!qualityCheckPreset)
    qualityCheckPreset = (await import("@/features/presets/preset").then((preset) =>
      preset.create({ staticImage: true }),
    )) as Preset

  /**
   *  For some reason face detection works not properly with images, it looks like it caches previous result / frame.
   *  So we need to run detection with blank image to 'skip' the previous frame.
   */
  const blankImage = new ImageData(1, 1)
  const blankImageSource = await createImageBitmap(blankImage)
  await qualityCheckPreset.getDetections(blankImageSource)
  blankImageSource.close?.()

  const imageSource = await createImageBitmap(image)
  const width = imageSource.width
  const height = imageSource.height
  const face = await qualityCheckPreset.getDetections(imageSource)
  const [illumination, qualityEst] = await Promise.all([
    face
      ? qualityCheckPreset.getIlluminationEstimation(face, imageBitmapToPixels(imageSource))
      : Promise.resolve(defaultIllumination),
    qualityCheckPreset.getQualityEstimation(imageSource, face),
  ])
  imageSource.close?.()

  const ctx = {
    detections: { face, illumination, quality: qualityEst },
    frame: { width, height },
    visibleRect: { x: 0, y: 0, width, height },
  }

  const isDesktop = checkDesktop()
  const coeff = isUpload && !isFace180 ? 0.4 : 0
  const faceCheckEnabled = features?.isOn("face-check") ?? false
  const { validate } = createImageValidator({
    // We've decided to increase the lighting direction and intensity coefficients for postprocessing checks.
    // Image passed to lighting check has large dimensions so the check is more sensitive.
    lightingDirectionCoeff: coeff,
    lightingIntensityCoeff: coeff,
    // We've decided not to check for blur and noise for desktop devices as it's not reliable. Check only for resolution.
    blurQualityCheckEnabled: !isDesktop || isUpload,
    // We've decided not to check for blur and noise for desktop devices as it's not reliable. Check only for resolution.
    noiseQualityCheckEnabled: !isDesktop || isUpload,
    // We've decided to disable face angle deviation check for 180 face as it's not reliable.
    faceAngleDeviationCheckEnabled: !isFace180,
    fullFaceVisibleCheckEnabled: !isFace180,
    // Separate face presence check - always enforced when face-check flag is enabled
    faceCheckEnabled,
  })
  return validate(ctx)
}
